// #region Imports
import { useMediaQuery } from "@mui/material";
import { styled, Theme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import {
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_TableInstance as TableInstance,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
} from "material-react-table";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { FeeScheduleTemplateLoadIssue } from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import { PageTab } from "work/fee-schedule-templates/_views/pages/tabs";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
// #endregion

// #region Styled Components
const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
// #endregion

// #region Props
type FeeScheduleToolbarProps = {
  table: TableInstance<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>;
  activeTab: PageTab;
};

export default function FeeScheduleToolbar(props: Readonly<FeeScheduleToolbarProps>) {
  const { table, activeTab } = props;

  // #region Hooks
  const navigate = useNavigate();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // #region Handlers
  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: PageTab) => {
    navigate(`/fee-schedules/${newValue}`);
  }, [navigate]);

  // #region Render
  return (
    <TableTopToolbarContainer>
      <TableTitle variant="h4">Fee Schedules</TableTitle>
      <LowerTopToolbarContainer>
        <Tabs
          value={activeTab}
          indicatorColor="primary"
          textColor="primary"
          onChange={handleTabChange}
        >
          <Tab value={PageTab.Templates} label="Templates" />
          <Tab value={PageTab.Active} label="Active" />
          <Tab value={PageTab.Archived} label="Archived" />
        </Tabs>
      </LowerTopToolbarContainer>
      {isMediumDisplaySize && (
        <ToolbarButtonsContainer>
          <ToolbarButtons>
            <ToggleGlobalFilterButton table={table} />
            <ToggleFiltersButton table={table} />
            <ShowHideColumnsButton table={table} />
            <ToggleDensePaddingButton table={table} />
            <ToggleFullScreenButton table={table} />
          </ToolbarButtons>
        </ToolbarButtonsContainer>
      )}
    </TableTopToolbarContainer>
  );
}
